export default {
  en: {
    nav: {
      home: 'Home',
      games: 'Games',
      leaderboard: 'Leaderboard',
      about: 'About',
      how: 'How to Play',
      modes: 'Modes',
      faq: 'FAQ',
      tips: 'Tips',
      support: 'Support',
      contact: 'Contact',
      privacy: 'Privacy',
      terms: 'Terms',
      strategies: 'Strategies'
    },
    buttons: {
      quickStart: 'Quick Start',
      play: 'Play Word',
      donate: 'Donate to pool',
      pass: 'Pass',
      backHome: 'Back to Home',
      restart: 'Restart',
      more: 'More',
      less: 'Less',
      check: 'Check',
      clear: 'Clear',
      hint: 'Hint',
      playAgain: 'Play Again',
      tryAgain: 'Try Again',
      next: 'Next',
      finish: 'Finish',
      reset: 'Reset Scores',
      view: 'View',
      send: 'Send',
      startPlaying: 'Start Playing'
    },
    placeholders: {
      enterWord: 'Enter a word'
    },
    hero: {
      title: 'Upgrade Your Brain, Start Smart Challenges',
      subtitle: 'Three popular games waiting for your challenge',
      cta: 'Start Playing Now'
    },
    games: {
      poker: {
        title: 'English Word Poker',
        description: 'Build words with letter cards and compete for points',
        difficulty: 'Easy to Hard'
      },
      sudoku: {
        title: 'Sudoku Challenge',
        description: 'Classic number puzzle with multiple difficulty levels',
        difficulty: 'Easy to Expert',
        selectDifficulty: 'Select Difficulty',
        easy: 'Easy',
        easyDesc: 'Perfect for beginners',
        medium: 'Medium',
        mediumDesc: 'Challenge yourself',
        hard: 'Hard',
        hardDesc: 'Expert level',
        congratulations: 'Congratulations! You solved it!',
        gameOver: 'Game Over',
        tooManyMistakes: 'You made too many mistakes. Try again!',
        solved: 'Puzzle solved! Well done!',
        notComplete: 'Please fill all cells before checking',
        incorrectSolution: 'Some numbers are incorrect. Keep trying!',
        invalidPlacement: 'Invalid number placement',
        hintProvided: 'Hint provided!',
        noMoreHints: 'You have used all your hints',
        noEmptyCells: 'All cells are filled'
      },
      riddles: {
        title: 'Brain Teasers',
        description: 'Test your wit with clever riddles and puzzles',
        difficulty: 'Medium to Hard',
        selectDifficulty: 'Select Difficulty',
        easy: 'Easy',
        easyDesc: 'Perfect for beginners',
        medium: 'Medium',
        mediumDesc: 'Challenge yourself',
        hard: 'Hard',
        hardDesc: 'Expert level',
        congratulations: 'Congratulations! You solved it!',
        goodJob: 'Good Job!',
        correctAnswers: 'Correct Answers',
        accuracy: 'Accuracy',
        questions: 'questions'
      }
    },
    features: {
      title: 'Why Choose UpGame?',
      feature1: {
        title: 'Learning & Fun Combined',
        description: 'Educational games that make learning enjoyable'
      },
      feature2: {
        title: 'Multiple Difficulty Levels',
        description: 'From beginner to expert, find your perfect challenge'
      },
      feature3: {
        title: 'Progress Tracking',
        description: 'Monitor your improvement and celebrate achievements'
      },
      feature4: {
        title: 'Achievement System',
        description: 'Unlock badges and rewards as you master each game'
      }
    },
    modes: {
      classic: 'Classic Mode',
      lightning: 'Lightning Mode',
      coop: 'Coop Mode',
      classicDesc: 'Take turns to build the best word each round. Perfect for strategic play.',
      lightningDesc: 'Beat the countdown and keep the streak going. Fast-paced and thrilling!',
      coopDesc: 'Work together. Donate letters to the shared pool for team victory.',
      classicDetails: ['Turn-based', '3-10 rounds', 'Score based'],
      lightningDetails: ['Real-time', '60s timer', 'Speed matters'],
      coopDetails: ['Team-based', 'Cooperation', 'Pool sharing']
    },
    game: {
      you: 'You',
      teammate: 'Teammate',
      // HUD strings
      hud: { round: 'Round', roundFmt: 'Round {cur}/{total}', turn: 'Turn', hand: 'Hand', deck: 'Deck', seconds: 's', team: 'Team', timer: 'Time', mistakes: 'Mistakes', score: 'Score' },
      selected: 'Selected',
      yourHand: 'Your hand',
      publicPool: 'Public pool',
      history: 'Game history',
      selectedHint: 'Click cards to toggle selection. Donate moves a single hand card into Public pool (Coop).',
      coopHint: 'Public pool fills only at the start of each turn. Donate to share letters with teammates.',
      definition: 'Definition',
      overlay: { resultTeam: 'Team Result', timeUp: 'Time Up', gameOver: 'Game Over' },
      labels: { fromHand: 'from hand', fromDeck: 'from deck', fromTeammate: 'from teammate' },
      progress: { target: 'Target', rounds: 'Rounds left' },
      noPlay: 'No play'
    },
    how: {
      title: 'How to Play',
      steps: [
        { title: 'Pick letters', text: 'Choose letters from your hand (and the Public pool in Coop).' },
        { title: 'Form a word', text: 'Make a valid word and press Play Word to score.' },
        { title: 'Restart or pass', text: 'Restart anytime, or use Pass if you get stuck.' }
      ]
    },
    faq: {
      pageTitle: 'Frequently Asked Questions',
      pageSubtitle: 'Find answers to common questions about UpGame',
      title: 'FAQ',
      stillNeedHelp: 'Still Need Help?',
      contactSupport: 'If you can\'t find the answer you\'re looking for, please contact our support team.',
      tabs: {
        platform: 'Platform'
      },
      items: [
        { q: 'Is UpGame free to play?', a: 'Yes, UpGame is completely free! All three games are available without any subscription or payment.' },
        { q: 'Do I need to create an account?', a: 'No account needed! Your scores are saved locally on your device using browser storage.' },
        { q: 'Can I play offline?', a: 'UpGame works best online, but many features work in offline mode. Restart your browser to refresh content.' },
        { q: 'Which browsers does UpGame support?', a: 'UpGame works on all modern browsers including Chrome, Firefox, Safari, and Edge on desktop and mobile devices.' }
      ],
      platform: {
        q1: 'Is UpGame free to play?',
        a1: 'Yes, UpGame is completely free! All three games are available without any subscription or payment.',
        q2: 'Do I need to create an account?',
        a2: 'No account needed! Your scores are saved locally on your device using browser storage.',
        q3: 'Can I play offline?',
        a3: 'UpGame works best online, but many features work in offline mode. Restart your browser to refresh content.',
        q4: 'Which browsers does UpGame support?',
        a4: 'UpGame works on all modern browsers including Chrome, Firefox, Safari, and Edge on desktop and mobile devices.'
      },
      poker: {
        q1: 'What counts as a valid word?',
        a1: 'Minimum 2 letters. We validate using a dictionary service during preview and on Cloudflare after deploy.',
        q2: 'How do I use letters from Public pool?',
        a2: 'In Coop mode, you can combine letters from your hand and the Public pool to form a word. The pool auto-fills only at the start of each turn.',
        q3: 'Can I donate multiple letters at once?',
        a3: 'Yes. Select one or more letters from your hand and click Donate to pool. Donated letters become available immediately.'
      },
      sudoku: {
        q1: 'How do I solve a Sudoku puzzle?',
        a1: 'Fill each row, column, and 3x3 box with numbers 1-9 without repeating. Each number appears exactly once in each row, column, and box.',
        q2: 'Can I get hints?',
        a2: 'Yes! You have a limited number of hints per game. Use them wisely to help solve difficult puzzles.',
        q3: 'What happens if I make a mistake?',
        a3: 'You can use the clear button to remove an entry, or restart the puzzle to start over from the beginning.'
      },
      riddles: {
        q1: 'How many riddles are there?',
        a1: 'We have a collection of riddles across three difficulty levels. More riddles are added regularly.',
        q2: 'Can I skip a riddle?',
        a2: 'Yes, you can skip riddles and move to the next one. Your accuracy score will reflect skipped riddles.',
        q3: 'What if I get the answer wrong?',
        a3: 'You can try again! Each riddle allows multiple attempts so you can think about it from different angles.'
      }
    },
    leaderboard: {
      title: 'My Leaderboard',
      subtitle: 'Track your personal scores and progress across all games',
      highScore: 'High Score',
      totalGames: 'Total Games',
      avgScore: 'Average Score',
      winRate: 'Win Rate',
      gameHistory: 'Game History',
      noScores: 'No scores yet. Start playing to see your stats!',
      confirmReset: 'Are you sure you want to reset all scores for this game?'
    },
    about: {
      title: 'About UpGame',
      subtitle: 'Learn more about our mission and values',
      mission: 'Our Mission',
      missionText: 'At UpGame, we believe learning should be fun and engaging. Our mission is to create educational games that challenge your mind while keeping you entertained. We\'re dedicated to making brain training accessible to everyone.',
      vision: 'Our Vision',
      visionText: 'We envision a world where education and entertainment blend seamlessly, where people of all ages can improve their cognitive skills through engaging games.',
      features: 'Why Choose UpGame?',
      feature1: {
        title: 'Free & Accessible',
        desc: 'No sign-up, no subscription. Just pure gaming fun!'
      },
      feature2: {
        title: 'Multi-Language Support',
        desc: 'Play in English, Chinese, or French'
      },
      feature3: {
        title: 'Mobile Friendly',
        desc: 'Play anywhere, anytime on any device'
      },
      feature4: {
        title: 'Score Tracking',
        desc: 'Monitor your progress and improvement'
      },
      ourGames: 'Our Games',
      getInTouch: 'Get In Touch',
      getInTouchText: 'Have questions or feedback? We\'d love to hear from you!'
    },
    howToPlay: {
      title: 'How to Play',
      subtitle: 'Learn the rules and get started with UpGame',
      objectiveLabel: 'Objective',
      howToPlayLabel: 'How to Play',
      rules: 'Rules',
      tips: 'Tips',
      modes: 'Game Modes',
      difficulties: 'Difficulty Levels',
      classicMode: 'Classic Mode',
      lightningMode: 'Lightning Mode',
      coopMode: 'Coop Mode',
      easyLevel: 'Easy',
      mediumLevel: 'Medium',
      hardLevel: 'Hard',
      ready: 'Ready to Play?',
      poker: {
        objective: 'Build the longest and highest-scoring words from the cards in your hand',
        step1: 'Look at the cards in your hand',
        step2: 'Arrange cards to form valid English words',
        step3: 'Click "Play Word" to submit and earn points',
        step4: 'Aim for the highest score before time runs out',
        classic: 'Take turns with other players to build words each round. The player with the best word scores points.',
        lightning: 'Race against the clock! Build words as fast as you can. Bonus points for speed!',
        coop: 'Work together! Both players contribute letters to a shared pool and cooperate to win.'
      },
      sudoku: {
        objective: 'Fill the grid so that each row, column, and 3×3 box contains all numbers 1-9',
        rule1: 'Each row must contain the digits 1 through 9 without repetition',
        rule2: 'Each column must contain the digits 1 through 9 without repetition',
        rule3: 'Each of the nine 3×3 sub-grids must contain the digits 1 through 9 without repetition',
        rule4: 'Some numbers are given as clues to start you off',
        tips: 'Start with rows, columns, or boxes that have fewer empty spaces. Look for numbers that can only go in one place.'
      },
      riddles: {
        objective: 'Read each riddle and guess the correct answer before moving to the next one',
        step1: 'Read the riddle carefully',
        step2: 'Think about the answer and type it in',
        step3: 'Check your answer and move to the next riddle',
        easy: 'Straightforward riddles with clear clues',
        medium: 'Riddles that require creative thinking',
        hard: 'Challenge riddles with tricky wordplay'
      }
    },
    tips: {
      title: 'Tips & Tricks',
      subtitle: 'Improve your game with strategy tips from our experts',
      poker: {
        tip1: { title: 'Look for Common Combinations', desc: 'Plan multiple words using your available letters to maximize points.' },
        tip2: { title: 'Use High-Value Letters', desc: 'Letters like Q, Z, and X are worth more points. Try to use them strategically.' },
        tip3: { title: 'Master Word Formation', desc: 'Practice common word patterns to form words faster.' },
        tip4: { title: 'Manage Your Time', desc: 'In Lightning mode, speed matters. Practice forming words quickly.' }
      },
      sudoku: {
        tip1: { title: 'Scan for Obvious Numbers', desc: 'Look for cells where only one number can fit.' },
        tip2: { title: 'Use the Process of Elimination', desc: 'Mark possible numbers for each cell and gradually eliminate options.' },
        tip3: { title: 'Focus on Constrained Areas', desc: 'Work on rows, columns, or boxes with fewer empty spaces first.' },
        tip4: { title: 'Take Your Time', desc: 'Sudoku is about logic, not speed. Think carefully before filling a cell.' }
      },
      riddles: {
        tip1: { title: 'Read Carefully', desc: 'Pay attention to every word in the riddle. The answer is often hidden in the wording.' },
        tip2: { title: 'Think Literally and Figuratively', desc: 'Riddles often use wordplay. Consider both literal and metaphorical meanings.' },
        tip3: { title: 'Avoid Common Answers', desc: 'Riddle answers are usually not the first thing that comes to mind.' },
        tip4: { title: 'Practice Makes Perfect', desc: 'The more riddles you solve, the better you\'ll get at spotting patterns.' }
      }
    },
    strategies: {
      title: 'Game Strategies',
      subtitle: 'Master the games with expert guides & tips',
      readMore: 'Read More',
      backToList: 'Back to Strategies',
      readyToPlay: 'Ready for the challenge?',
      notFound: 'Article not found'
    },
    support: {
      title: 'Support & Help',
      subtitle: 'We\'re here to help you with any questions',
      quickHelp: 'Quick Help',
      faq: 'Frequently Asked Questions',
      faqDesc: 'Find answers to the most common questions about UpGame',
      howToPlay: 'How to Play',
      howToPlayDesc: 'Learn the rules and how to get started with each game',
      tips: 'Tips & Tricks',
      tipsDesc: 'Improve your skills with strategy tips from our experts',
      commonIssues: 'Common Issues',
      stillNeedHelp: 'Still Need Help?',
      contactUsDesc: 'If you can\'t find what you\'re looking for, please contact us.',
      issue1: { title: 'Scores not saving', solution: 'Make sure your browser allows local storage. Check your privacy settings and ensure cookies/storage are enabled.' },
      issue2: { title: 'Game not loading', solution: 'Try refreshing the page or clearing your browser cache. Make sure you have the latest version of your browser.' },
      issue3: { title: 'Words not being accepted', solution: 'Ensure you\'re forming valid English words with minimum 2 letters. Check our dictionary for accepted words.' },
      issue4: { title: 'Performance issues', solution: 'Close other browser tabs and applications. Disable browser extensions that might interfere with games.' }
    },
    contact: {
      title: 'Contact Us',
      subtitle: 'We\'d love to hear from you. Get in touch with our team.',
      sendMessage: 'Send us a Message',
      name: 'Name',
      namePlaceholder: 'Your name',
      email: 'Email',
      emailPlaceholder: 'Your email address',
      subject: 'Subject',
      selectSubject: 'Select a subject',
      subjectBug: 'Bug Report',
      subjectFeature: 'Feature Request',
      subjectFeedback: 'General Feedback',
      subjectOther: 'Other',
      message: 'Message',
      messagePlaceholder: 'Tell us what you think...',
      getInTouch: 'Get in Touch',
      website: 'Website',
      responseTime: 'Response Time',
      responseTimeDesc: 'We typically respond to messages within 24 hours',
      successMessage: 'Thank you for your message! We\'ll get back to you soon.',
      errorMessage: 'There was an error sending your message. Please try again.'
    },
    privacy: {
      title: 'Privacy Policy',
      lastUpdated: 'Last updated: 2025-01-01',
      introduction: 'Introduction',
      introductionText: 'At UpGame (upgame.top), we respect your privacy and are committed to protecting your personal data. This Privacy Policy explains how we collect, use, and safeguard your information.',
      dataCollection: 'Data Collection',
      dataCollectionText: 'We collect minimal personal information to provide our services. All data is stored locally on your device.',
      typesOfData: 'Types of Data We Collect',
      data1: 'Game scores and statistics (stored locally)',
      data2: 'Language and theme preferences',
      data3: 'Browser and device information for analytics',
      data4: 'Contact form submissions (email and message only)',
      dataUsage: 'How We Use Your Data',
      dataUsageText: 'Your data is used solely to improve your gaming experience and our services.',
      purposes: 'Purposes of Data Usage',
      purpose1: 'To track your game progress and scores',
      purpose2: 'To remember your preferences and settings',
      purpose3: 'To respond to your contact form submissions',
      purpose4: 'To improve our games based on usage patterns',
      dataStorage: 'Data Storage',
      dataStorageText: 'Most of your data is stored locally in your browser. Contact form submissions are stored securely on our servers for a limited time.',
      userRights: 'Your Rights',
      userRightsText: 'You have the right to control your personal information. Here\'s what you can do:',
      rights: 'Your Rights',
      right1: 'Access your data anytime through your browser',
      right2: 'Delete your data by clearing your browser storage',
      right3: 'Export your data for personal use',
      right4: 'Opt-out of analytics tracking in settings',
      contactUs: 'Contact Us',
      contactUsText: 'If you have any questions about this Privacy Policy, please contact us:'
    },
    terms: {
      title: 'Terms of Service',
      lastUpdated: 'Last updated: 2025-01-01',
      acceptance: 'Acceptance of Terms',
      acceptanceText: 'By accessing and using UpGame, you accept and agree to be bound by the terms and provision of this agreement. If you do not agree to abide by the above, please do not use this service.',
      userAgreement: 'User Agreement',
      userAgreementText: 'Users of UpGame agree to use the service only for lawful purposes and in a way that does not infringe upon the rights of others or restrict their use and enjoyment.',
      requirements: 'User Requirements',
      requirement1: 'You must be at least 13 years old to use UpGame',
      requirement2: 'You are responsible for maintaining the confidentiality of your device',
      requirement3: 'You agree not to engage in any illegal or harmful activities',
      requirement4: 'You will not attempt to gain unauthorized access to the service',
      useRights: 'Limited License',
      useRightsText: 'UpGame grants you a limited, non-exclusive, non-transferable license to use the service for personal, non-commercial use only.',
      restrictions: 'Restrictions',
      restrictionsText: 'You agree not to engage in any of the following prohibited conduct:',
      prohibited: 'Prohibited Activities',
      prohibited1: 'Reverse engineering or attempting to modify the service',
      prohibited2: 'Using the service for commercial purposes without permission',
      prohibited3: 'Attempting to access or tamper with the service infrastructure',
      prohibited4: 'Creating multiple accounts to circumvent limitations',
      intellectualProperty: 'Intellectual Property',
      intellectualPropertyText: 'All content, features, and functionality of UpGame are owned by UpGame, its licensors, or other providers of such material and are protected by copyright, trademark, and other intellectual property laws.',
      disclaimers: 'Disclaimer of Warranties',
      disclaimersText: 'UpGame is provided on an \"AS IS\" basis without warranties of any kind. We do not warrant that the service will be uninterrupted or error-free.',
      limitation: 'Limitation of Liability',
      limitationText: 'In no event shall UpGame be liable for any indirect, incidental, special, consequential, or punitive damages resulting from your use of or inability to use the service.',
      termination: 'Termination',
      terminationText: 'We may terminate or suspend your access to UpGame at any time, with or without cause, without notice or liability.',
      modifications: 'Modifications to Terms',
      modificationsText: 'We reserve the right to modify these terms at any time. Changes will be effective immediately upon posting. Your continued use constitutes acceptance of modified terms.',
      governingLaw: 'Governing Law',
      governingLawText: 'These Terms of Service are governed by and construed in accordance with the laws of the jurisdiction where UpGame is operated.',
      contactUs: 'Contact Us',
      contactUsText: 'If you have any questions about these Terms, please contact us:'
    },
    footer: {
      description: 'Upgrade your brain with fun and educational games. Challenge yourself with word puzzles, number games, and brain teasers.',
      games: 'Games',
      resources: 'Resources',
      company: 'Company',
      howToPlay: 'How to Play',
      tips: 'Tips & Tricks',
      faq: 'FAQ',
      support: 'Support',
      about: 'About Us',
      contact: 'Contact',
      privacy: 'Privacy Policy',
      terms: 'Terms of Service',
      allRightsReserved: 'All rights reserved.',
      madeWith: 'Made with',
      forLearning: 'for learning and fun',
      language: 'Language'
    },
    dict: { loading: 'Loading definition...', unavailable: 'Definition unavailable.', more: 'More', less: 'Less' },
    cookie: {
      description: 'We use cookies to improve your experience and analyze site traffic. By clicking "Accept", you agree to our use of cookies.',
      accept: 'Accept',
      decline: 'Decline'
    }
  },

  zh: {
    nav: {
      home: '首页',
      games: '游戏',
      leaderboard: '排行榜',
      about: '关于',
      how: '玩法说明',
      modes: '模式',
      faq: '常见问题',
      tips: '技巧',
      support: '支持',
      contact: '联系我们',
      privacy: '隐私政策',
      terms: '条款',
      strategies: '攻略'
    },
    buttons: {
      quickStart: '快速开始',
      play: '出词',
      donate: '捐献到公共池',
      pass: '过牌',
      backHome: '返回首页',
      restart: '重开',
      more: '更多',
      less: '收起',
      check: '检查',
      clear: '清除',
      hint: '提示',
      playAgain: '重新游戏',
      tryAgain: '再试一次',
      next: '下一个',
      finish: '完成',
      reset: '重置分数',
      view: '查看',
      send: '发送',
      startPlaying: '开始游戏'
    },
    placeholders: {
      enterWord: '输入单词'
    },
    hero: {
      title: '升级你的大脑，开启智慧挑战',
      subtitle: '三大热门游戏，等你来挑战',
      cta: '立即开始游戏'
    },
    games: {
      poker: {
        title: '英语单词扑克',
        description: '用字母卡片组成单词，竞争得分',
        difficulty: '简单到困难'
      },
      sudoku: {
        title: '数独挑战',
        description: '经典数字谜题，多种难度等级',
        difficulty: '简单到专家',
        selectDifficulty: '选择难度',
        easy: '简单',
        easyDesc: '适合初学者',
        medium: '中等',
        mediumDesc: '挑战自己',
        hard: '困难',
        hardDesc: '专家级别',
        congratulations: '恭喜！你解决了！',
        gameOver: '游戏结束',
        tooManyMistakes: '你犯了太多错误。请重试！',
        solved: '谜题已解决！干得好！',
        notComplete: '请在检查前填满所有格子',
        incorrectSolution: '有些数字不正确。继续加油！',
        invalidPlacement: '无效的数字位置',
        hintProvided: '提示已提供！',
        noMoreHints: '你已使用所有提示',
        noEmptyCells: '所有格子已填满'
      },
      riddles: {
        title: '脑筋急转弯',
        description: '用巧妙的谜题和智力题测试你的机智',
        difficulty: '中等到困难',
        selectDifficulty: '选择难度',
        easy: '简单',
        easyDesc: '适合初学者',
        medium: '中等',
        mediumDesc: '挑战自己',
        hard: '困难',
        hardDesc: '专家级别',
        congratulations: '恭喜！你解决了！',
        goodJob: '干得不错！',
        correctAnswers: '正确答案',
        accuracy: '准确率',
        questions: '题目'
      }
    },
    features: {
      title: '为什么选择 UpGame？',
      feature1: {
        title: '学习与娱乐结合',
        description: '寓教于乐的教育游戏，让学习变得有趣'
      },
      feature2: {
        title: '多种难度选择',
        description: '从初学者到专家，找到你的完美挑战'
      },
      feature3: {
        title: '进度追踪',
        description: '监控你的进步并庆祝成就'
      },
      feature4: {
        title: '成就系统',
        description: '掌握每个游戏时解锁徽章和奖励'
      }
    },
    modes: {
      classic: '经典模式',
      lightning: '闪电模式',
      coop: '协作模式',
      classicDesc: '轮流出词，比拼每回合的最佳单词。完美适合战术玩法。',
      lightningDesc: '与倒计时赛跑，保持连胜节奏。快速紧张刺激！',
      coopDesc: '一起协作，把手牌捐到公共池，合力达成团队目标。',
      classicDetails: ['轮流制', '3-10回合', '分数制'],
      lightningDetails: ['实时制', '60秒计时', '速度至上'],
      coopDetails: ['团队制', '合作协力', '共享池']
    },
    game: {
      you: '你',
      teammate: '队友',
      // HUD strings
      hud: { round: '回合', roundFmt: '回合 {cur}/{total}', turn: '轮到', hand: '手牌', deck: '牌堆', seconds: '秒', team: '团队', timer: '时间', mistakes: '错误', score: '得分' },
      selected: '已选字母',
      yourHand: '你的手牌',
      publicPool: '公共池',
      history: '出词历史',
      selectedHint: '点击卡牌进行选择/取消。捐献会把一张手牌放入公共池（协作模式）。',
      coopHint: '公共池仅在每回合开始时自动补满；回合进行中不会补齐。请用捐献共享字母给队友。',
      definition: '释义',
      overlay: { resultTeam: '团队结果', timeUp: '时间到', gameOver: '游戏结束' },
      labels: { fromHand: '手牌捐献', fromDeck: '牌堆补充', fromTeammate: '队友捐献' },
      progress: { target: '目标', rounds: '剩余回合' },
      noPlay: '未出词'
    },
    how: {
      title: '玩法说明',
      steps: [
        { title: '挑选字母', text: '从你的手牌选择字母（协作模式可用公共池）。' },
        { title: '组合成词', text: '拼出合法单词并点击出词。' },
        { title: '重开或过牌', text: '随时重开，或在卡手时选择过牌。' }
      ]
    },
    faq: {
      pageTitle: '常见问题',
      pageSubtitle: '查找关于 UpGame 的常见问题答案',
      title: '常见问题',
      stillNeedHelp: '还需要帮助？',
      contactSupport: '如果您找不到所需的答案，请联系我们的支持团队。',
      tabs: {
        platform: '平台'
      },
      items: [
        { q: 'UpGame 是免费的吗?', a: '是的，UpGame 完全免费！所有三款游戏都可以不付款或不订阅而使用。' },
        { q: '我需要创建账户吗？', a: '不需要账户！你的分数通过浏览器存储保存在你的设备上。' },
        { q: '我可以离线玩吗？', a: 'UpGame 在线效果最好，但很多功能在离线模式下也能使用。重启浏览器以刷新内容。' },
        { q: 'UpGame 支持哪些浏览器？', a: 'UpGame 支持所有现代浏览器，包括 Chrome、Firefox、Safari 和 Edge（台式电脑和移动设备）。' }
      ],
      platform: {
        q1: 'UpGame 是免费的吗？',
        a1: '是的，UpGame 完全免费！所有三款游戏都可以不付款或不订阅而使用。',
        q2: '我需要创建账户吗？',
        a2: '不需要账户！你的分数通过浏览器存储保存在你的设备上。',
        q3: '我可以离线玩吗？',
        a3: 'UpGame 在线效果最好，但很多功能在离线模式下也能使用。重启浏览器以刷新内容。',
        q4: 'UpGame 支持哪些浏览器？',
        a4: 'UpGame 支持所有现代浏览器，包括 Chrome、Firefox、Safari 和 Edge（台式电脑和移动设备）。'
      },
      poker: {
        q1: '什么算有效的单词？',
        a1: '至少 2 个字母。我们在预览时和部署后在 Cloudflare 上使用词典服务进行验证。',
        q2: '如何使用公共池中的字母？',
        a2: '在协作模式中，您可以结合手中的字母和公共池来组成单词。公共池仅在每回合开始时自动填充。',
        q3: '我可以一次捐献多个字母吗？',
        a3: '可以。从手中选择一个或多个字母并点击捐献到池。捐献的字母将立即可用。'
      },
      sudoku: {
        q1: '如何解决数独谜题？',
        a1: '填充每行、每列和 3x3 框，使其包含数字 1-9，不重复。每个数字在每行、每列和每框中只出现一次。',
        q2: '我可以获得提示吗？',
        a2: '可以！每局游戏您有有限数量的提示。明智地使用它们来帮助解决困难的谜题。',
        q3: '如果我犯了错误怎么办？',
        a3: '您可以使用清除按钮删除条目，或重新开始谜题从头开始。'
      },
      riddles: {
        q1: '有多少个谜语？',
        a1: '我们有三个难度级别的谜语集合。我们会定期添加更多谜语。',
        q2: '我可以跳过谜语吗？',
        a2: '可以，您可以跳过谜语并移至下一个。您的准确度分数将反映跳过的谜语。',
        q3: '如果我答错了怎么办？',
        a3: '您可以再试一次！每个谜语允许多次尝试，因此您可以从不同角度思考。'
      }
    },
    leaderboard: {
      title: '我的排行榜',
      subtitle: '跟踪你在所有游戏中的个人分数和进度',
      highScore: '最高分',
      totalGames: '总游戏数',
      avgScore: '平均分',
      winRate: '胜率',
      gameHistory: '游戏历史',
      noScores: '还没有分数。开始玩游戏来查看你的统计数据！',
      confirmReset: '你确定要重置此游戏的所有分数吗？'
    },
    about: {
      title: '关于UpGame',
      subtitle: '了解我们的使命和价值观',
      mission: '我们的使命',
      missionText: '在UpGame，我们相信学习应该是有趣和有吸引力的。我们的使命是创建具有挑战性的教育游戏，在保持您娱乐的同时锻炼您的思维。我们致力于让大脑训练对所有人都可用。',
      vision: '我们的愿景',
      visionText: '我们设想一个教育和娱乐无缝融合的世界，其中各个年龄的人都能通过有吸引力的游戏来改善他们的认知技能。',
      features: '为什么选择UpGame？',
      feature1: {
        title: '免费且可访问',
        desc: '无需注册，无需订阅。纯粹的游戏乐趣！'
      },
      feature2: {
        title: '多语言支持',
        desc: '用英文、中文或法文玩游戏'
      },
      feature3: {
        title: '移动友好',
        desc: '随时随地在任何设备上玩'
      },
      feature4: {
        title: '分数追踪',
        desc: '监控你的进度和改进'
      },
      ourGames: '我们的游戏',
      getInTouch: '与我们联系',
      getInTouchText: '有问题或反馈？我们很乐意听取你的意见！'
    },
    howToPlay: {
      title: '玩法说明',
      subtitle: '学习规则并开始使用UpGame',
      objectiveLabel: '目标',
      howToPlayLabel: '玩法',
      rules: '规则',
      tips: '提示',
      modes: '游戏模式',
      difficulties: '难度级别',
      classicMode: '经典模式',
      lightningMode: '闪电模式',
      coopMode: '协作模式',
      easyLevel: '简单',
      mediumLevel: '中等',
      hardLevel: '困难',
      ready: '准备好了吗？',
      poker: {
        objective: '从手中的卡片组成最长和最高分的单词',
        step1: '看看你手中的卡片',
        step2: '整理卡片组成有效的英文单词',
        step3: '点击"出词"提交并获得分数',
        step4: '在时间用尽前争取最高分',
        classic: '与其他玩家轮流在每一轮中组建单词。最好的单词获胜的玩家得分。',
        lightning: '与时钟竞争！尽快组建单词。速度快奖励加分！',
        coop: '一起合作！两名玩家都将字母贡献给共享池并合作赢得。'
      },
      sudoku: {
        objective: '填充网格，使每行、每列和3×3框都包含所有数字1-9',
        rule1: '每行必须包含数字1到9，不重复',
        rule2: '每列必须包含数字1到9，不重复',
        rule3: '每个九个3×3子网格必须包含数字1到9，不重复',
        rule4: '有些数字作为线索给出来开始',
        tips: '从空格较少的行、列或框开始。查找只能放在一个地方的数字。'
      },
      riddles: {
        objective: '仔细阅读每个谜语，在继续下一个之前猜出正确答案',
        step1: '仔细阅读谜语',
        step2: '思考答案并输入',
        step3: '检查你的答案并继续下一个谜语',
        easy: '有清晰线索的简单谜语',
        medium: '需要创意思维的谜语',
        hard: '有棘手文字游戏的挑战谜语'
      }
    },
    tips: {
      title: '技巧和窍门',
      subtitle: '通过我们专家的策略技巧改进你的游戏',
      poker: {
        tip1: { title: '寻找常见组合', desc: '使用你可用的字母计划多个单词以最大化分数。' },
        tip2: { title: '使用高价值字母', desc: 'Q、Z和X等字母价值更高。尝试策略性地使用它们。' },
        tip3: { title: '掌握单词形成', desc: '练习常见的单词模式以更快地形成单词。' },
        tip4: { title: '管理你的时间', desc: '在闪电模式中，速度很重要。练习快速组建单词。' }
      },
      sudoku: {
        tip1: { title: '扫描明显的数字', desc: '寻找只能放入一个数字的单元格。' },
        tip2: { title: '使用排除法', desc: '标记每个单元格的可能数字并逐渐消除选项。' },
        tip3: { title: '专注于受限的区域', desc: '首先处理空格较少的行、列或框。' },
        tip4: { title: '花费时间', desc: '数独是关于逻辑的，而不是速度。在填充单元格之前仔细思考。' }
      },
      riddles: {
        tip1: { title: '仔细阅读', desc: '注意谜语中的每一个词。答案通常隐藏在措辞中。' },
        tip2: { title: '从字面和比喻上思考', desc: '谜语经常使用文字游戏。考虑字面和隐喻含义。' },
        tip3: { title: '避免常见答案', desc: '谜语答案通常不是首先想到的东西。' },
        tip4: { title: '熟能生巧', desc: '你解决的谜语越多，你就越擅长发现模式。' }
      }
    },
    strategies: {
      title: '游戏攻略',
      subtitle: '专家级指南带你掌握游戏精髓',
      readMore: '阅读全文',
      backToList: '返回攻略列表',
      readyToPlay: '准备接受挑战了吗？',
      notFound: '文章未找到'
    },
    support: {
      title: '支持和帮助',
      subtitle: '我们在这里帮助你解答任何问题',
      quickHelp: '快速帮助',
      faq: '常见问题',
      faqDesc: '找到关于UpGame最常见问题的答案',
      howToPlay: '玩法说明',
      howToPlayDesc: '学习规则和如何开始每个游戏',
      tips: '技巧和窍门',
      tipsDesc: '通过我们专家的策略技巧改进你的技能',
      commonIssues: '常见问题',
      stillNeedHelp: '仍需帮助？',
      contactUsDesc: '如果你找不到你要的东西，请与我们联系。',
      issue1: { title: '分数不保存', solution: '确保你的浏览器允许本地存储。检查你的隐私设置并确保启用了cookies/存储。' },
      issue2: { title: '游戏加载不了', solution: '尝试刷新页面或清除浏览器缓存。确保你有最新版本的浏览器。' },
      issue3: { title: '单词不被接受', solution: '确保你形成有效的英文单词，最少2个字母。查看我们的字典了解接受的单词。' },
      issue4: { title: '性能问题', solution: '关闭其他浏览器标签和应用程序。禁用可能干扰游戏的浏览器扩展。' }
    },
    contact: {
      title: '联系我们',
      subtitle: '我们很乐意听取你的意见。与我们的团队联系。',
      sendMessage: '给我们发送消息',
      name: '名字',
      namePlaceholder: '你的名字',
      email: '邮箱',
      emailPlaceholder: '你的邮箱地址',
      subject: '主题',
      selectSubject: '选择主题',
      subjectBug: '错误报告',
      subjectFeature: '功能请求',
      subjectFeedback: '一般反馈',
      subjectOther: '其他',
      message: '消息',
      messagePlaceholder: '告诉我们你的想法...',
      getInTouch: '与我们联系',
      website: '网站',
      responseTime: '响应时间',
      responseTimeDesc: '我们通常在24小时内回复消息',
      successMessage: '感谢你的消息！我们很快就会回复你。',
      errorMessage: '发送消息时出错。请重试。'
    },
    privacy: {
      title: '隐私政策',
      lastUpdated: '最后更新：2025-01-01',
      introduction: '介绍',
      introductionText: '在UpGame（upgame.top），我们尊重你的隐私并致力于保护你的个人数据。本隐私政策解释了我们如何收集、使用和保护你的信息。',
      dataCollection: '数据收集',
      dataCollectionText: '我们收集最少的个人信息来提供我们的服务。所有数据都存储在你的设备上本地。',
      typesOfData: '我们收集的数据类型',
      data1: '游戏分数和统计数据（存储在本地）',
      data2: '语言和主题偏好',
      data3: '浏览器和设备信息用于分析',
      data4: '联系表单提交（仅限电子邮件和消息）',
      dataUsage: '我们如何使用你的数据',
      dataUsageText: '你的数据仅用于改进你的游戏体验和我们的服务。',
      purposes: '数据使用目的',
      purpose1: '跟踪你的游戏进度和分数',
      purpose2: '记住你的偏好和设置',
      purpose3: '回复你的联系表单提交',
      purpose4: '根据使用模式改进我们的游戏',
      dataStorage: '数据存储',
      dataStorageText: '大多数数据存储在你的浏览器中本地。联系表单提交安全存储在我们的服务器上一段时间。',
      userRights: '你的权利',
      userRightsText: '你有权控制你的个人信息。这是你可以做的：',
      rights: '你的权利',
      right1: '随时通过浏览器访问你的数据',
      right2: '通过清除浏览器存储来删除你的数据',
      right3: '导出你的数据以供个人使用',
      right4: '在设置中选择退出分析跟踪',
      contactUs: '联系我们',
      contactUsText: '如果你对本隐私政策有任何疑问，请与我们联系：'
    },
    terms: {
      title: '服务条款',
      lastUpdated: '最后更新：2025-01-01',
      acceptance: '接受条款',
      acceptanceText: '通过访问和使用UpGame，你接受并同意受本协议条款和规定的约束。如果你不同意遵守上述条款，请不要使用本服务。',
      userAgreement: '用户协议',
      userAgreementText: 'UpGame的用户同意仅为合法目的使用该服务，其方式不会侵犯他人的权利或限制他人的使用和享受。',
      requirements: '用户要求',
      requirement1: '你必须年满13岁才能使用UpGame',
      requirement2: '你负责维护你的设备的保密性',
      requirement3: '你同意不从事任何非法或有害的活动',
      requirement4: '你不会尝试未经授权访问该服务',
      useRights: '有限许可',
      useRightsText: 'UpGame授予你一个有限的、非独占的、不可转让的许可，仅供个人非商业使用。',
      restrictions: '限制',
      restrictionsText: '你同意不从事任何以下禁止行为：',
      prohibited: '禁止的活动',
      prohibited1: '逆向工程或试图修改服务',
      prohibited2: '在没有许可的情况下将服务用于商业目的',
      prohibited3: '尝试访问或篡改服务基础设施',
      prohibited4: '创建多个帐户以规避限制',
      intellectualProperty: '知识产权',
      intellectualPropertyText: 'UpGame的所有内容、功能和功能都由UpGame、其许可方或其他此类材料提供方拥有，并受版权、商标和其他知识产权法保护。',
      disclaimers: '免责声明',
      disclaimersText: 'UpGame按"原样"提供，不提供任何形式的保证。我们不保证该服务将不间断或无错误。',
      limitation: '责任限制',
      limitationText: 'UpGame对因你使用或无法使用服务而产生的任何间接、附带、特殊、后果性或惩罚性损害赔偿概不负责。',
      termination: '终止',
      terminationText: '我们可以随时以任何原因或无任何原因、不通知或不承担责任地终止或暂停你对UpGame的访问。',
      modifications: '条款修改',
      modificationsText: '我们保留随时修改这些条款的权利。更改将在发布后立即生效。你继续使用表示接受修改后的条款。',
      governingLaw: '管辖法律',
      governingLawText: '这些服务条款由UpGame运营所在司法管辖区的法律管辖和解释。',
      contactUs: '联系我们',
      contactUsText: '如果你对这些条款有任何疑问，请与我们联系：'
    },
    footer: {
      description: '通过有趣的教育游戏升级你的大脑。用单词谜题、数字游戏和脑筋急转弯挑战自己。',
      games: '游戏',
      resources: '资源',
      company: '公司',
      howToPlay: '玩法说明',
      tips: '技巧与窍门',
      faq: '常见问题',
      support: '支持',
      about: '关于我们',
      contact: '联系我们',
      privacy: '隐私政策',
      terms: '服务条款',
      allRightsReserved: '版权所有。',
      madeWith: '用',
      forLearning: '制作，为了学习和乐趣',
      language: '语言'
    },
    dict: { loading: '正在加载释义...', unavailable: '暂无释义。', more: '更多', less: '收起' },
    cookie: {
      description: '我们使用 Cookie 来改善您的体验并分析网站流量。点击“接受”即表示您同意我们使用 Cookie。',
      accept: '接受',
      decline: '拒绝'
    }
  },

  fr: {
    nav: {
      home: 'Accueil',
      games: 'Jeux',
      leaderboard: 'Classement',
      about: 'À propos',
      how: 'Guide',
      modes: 'Modes',
      faq: 'FAQ',
      strategies: 'Stratégies'
    },
    buttons: {
      quickStart: 'Démarrage rapide',
      play: 'Valider le mot',
      donate: 'Donner à la réserve',
      pass: 'Passer',
      backHome: 'Retour à l\'accueil',
      restart: 'Relancer',
      more: 'Plus',
      less: 'Moins',
      check: 'Vérifier',
      clear: 'Effacer',
      hint: 'Indice',
      playAgain: 'Rejouer',
      tryAgain: 'Réessayer',
      next: 'Suivant',
      finish: 'Terminer'
    },
    placeholders: {
      enterWord: 'Saisir un mot'
    },
    hero: {
      title: 'Améliorez votre cerveau, commencez des défis intelligents',
      subtitle: 'Trois jeux populaires vous attendent pour le défi',
      cta: 'Commencer à jouer maintenant'
    },
    games: {
      poker: {
        title: 'Poker de mots anglais',
        description: 'Construisez des mots avec des cartes lettres et rivalisez pour des points',
        difficulty: 'Facile à difficile'
      },
      sudoku: {
        title: 'Défi Sudoku',
        description: 'Puzzle de nombres classique avec plusieurs niveaux de difficulté',
        difficulty: 'Facile à expert',
        selectDifficulty: 'Sélectionner la difficulté',
        easy: 'Facile',
        easyDesc: 'Parfait pour les débutants',
        medium: 'Moyen',
        mediumDesc: 'Défiez-vous',
        hard: 'Difficile',
        hardDesc: 'Niveau expert',
        congratulations: 'Félicitations ! Vous l\'avez résolu !',
        gameOver: 'Fin de partie',
        tooManyMistakes: 'Vous avez fait trop d\'erreurs. Réessayez !',
        solved: 'Puzzle résolu ! Bien joué !',
        notComplete: 'Veuillez remplir toutes les cases avant de vérifier',
        incorrectSolution: 'Certains nombres sont incorrects. Continuez !',
        invalidPlacement: 'Placement de nombre invalide',
        hintProvided: 'Indice fourni !',
        noMoreHints: 'Vous avez utilisé tous vos indices',
        noEmptyCells: 'Toutes les cases sont remplies'
      },
      riddles: {
        title: 'Casse-têtes',
        description: 'Testez votre esprit avec des énigmes et puzzles astucieux',
        difficulty: 'Moyen à difficile',
        selectDifficulty: 'Sélectionner la difficulté',
        easy: 'Facile',
        easyDesc: 'Parfait pour les débutants',
        medium: 'Moyen',
        mediumDesc: 'Défiez-vous',
        hard: 'Difficile',
        hardDesc: 'Niveau expert',
        congratulations: 'Félicitations ! Vous l\'avez résolu !',
        goodJob: 'Bien joué !',
        correctAnswers: 'Bonnes réponses',
        accuracy: 'Précision',
        questions: 'questions'
      }
    },
    features: {
      title: 'Pourquoi choisir UpGame ?',
      feature1: {
        title: 'Apprentissage et amusement combinés',
        description: 'Des jeux éducatifs qui rendent l\'apprentissage agréable'
      },
      feature2: {
        title: 'Niveaux de difficulté multiples',
        description: 'Du débutant à l\'expert, trouvez votre défi parfait'
      },
      feature3: {
        title: 'Suivi de la progression',
        description: 'Suivez vos améliorations et célébrez vos réussites'
      },
      feature4: {
        title: 'Système d\'accomplissements',
        description: 'Déverrouillez des badges et des récompenses en maîtrisant chaque jeu'
      }
    },
    modes: {
      classic: 'Mode Classique',
      lightning: 'Mode Éclair',
      coop: 'Mode Coop',
      classicDesc: 'À tour de rôle, formez le meilleur mot de chaque manche. Parfait pour les stratèges.',
      lightningDesc: 'Battez le chrono et gardez le rythme. Rapide et palpitant!',
      coopDesc: 'Collaborez: donnez des lettres à la réserve publique pour la victoire en équipe.',
      classicDetails: ['Par tour', '3-10 manches', 'Basé sur le score'],
      lightningDetails: ['Temps réel', 'Chrono 60s', 'La vitesse compte'],
      coopDetails: ['En équipe', 'Coopération', 'Partage de réserve']
    },
    game: {
      you: 'Vous',
      teammate: 'Coéquipier',
      hud: { round: 'Manche', turn: 'Tour', hand: 'Main', deck: 'Paquet', seconds: 's', team: 'Équipe', timer: 'Temps', mistakes: 'Erreurs', score: 'Score' },
      selected: 'Sélectionné',
      yourHand: 'Votre main',
      publicPool: 'Réserve publique',
      history: 'Historique',
      selectedHint: 'Cliquez pour sélectionner/désélectionner. Donner envoie une carte de la main vers la réserve (mode Coop).',
      coopHint: 'La réserve publique se remplit uniquement au début de chaque tour. Donnez des lettres pour aider votre équipe.',
      definition: 'Définition',
      overlay: { resultTeam: 'Résultat d\'équipe', timeUp: 'Temps écoulé', gameOver: 'Partie terminée' },
      labels: { fromHand: 'depuis la main', fromDeck: 'depuis le paquet', fromTeammate: 'd\'un coéquipier' },
      progress: { target: 'Objectif', rounds: 'Manches restantes' },
      noPlay: 'Pas joué'
    },
    how: {
      title: 'Guide',
      steps: [
        { title: 'Choisir des lettres', text: 'Sélectionnez les lettres de votre main (et de la réserve en Coop).' },
        { title: 'Former un mot', text: 'Formez un mot valide puis cliquez Valider le mot.' },
        { title: 'Relancer ou passer', text: 'Redémarrez à tout moment, ou passez si vous êtes bloqué.' }
      ]
    },
    faq: {
      pageTitle: 'Foire aux questions',
      pageSubtitle: 'Trouvez des réponses aux questions courantes sur UpGame',
      title: 'FAQ',
      stillNeedHelp: 'Vous avez toujours besoin d\'aide ?',
      contactSupport: 'Si vous ne trouvez pas la réponse que vous cherchez, veuillez nous contacter.',
      tabs: {
        platform: 'Plateforme'
      },
      items: [
        { q: 'UpGame est-il gratuit ?', a: 'Oui, UpGame est complètement gratuit ! Les trois jeux sont disponibles sans abonnement ni paiement.' },
        { q: 'Dois-je créer un compte ?', a: 'Non, pas besoin de compte ! Vos scores sont enregistrés localement sur votre appareil en utilisant le stockage du navigateur.' },
        { q: 'Puis-je jouer hors ligne ?', a: 'UpGame fonctionne mieux en ligne, mais de nombreuses fonctionnalités fonctionnent en mode hors ligne. Redémarrez votre navigateur pour actualiser le contenu.' },
        { q: 'Quels navigateurs UpGame supporte-t-il ?', a: 'UpGame fonctionne sur tous les navigateurs modernes, notamment Chrome, Firefox, Safari et Edge sur les appareils de bureau et mobiles.' }
      ],
      platform: {
        q1: 'UpGame est-il gratuit ?',
        a1: 'Oui, UpGame est complètement gratuit ! Les trois jeux sont disponibles sans abonnement ni paiement.',
        q2: 'Dois-je créer un compte ?',
        a2: 'Non, pas besoin de compte ! Vos scores sont enregistrés localement sur votre appareil en utilisant le stockage du navigateur.',
        q3: 'Puis-je jouer hors ligne ?',
        a3: 'UpGame fonctionne mieux en ligne, mais de nombreuses fonctionnalités fonctionnent en mode hors ligne. Redémarrez votre navigateur pour actualiser le contenu.',
        q4: 'Quels navigateurs UpGame supporte-t-il ?',
        a4: 'UpGame fonctionne sur tous les navigateurs modernes, notamment Chrome, Firefox, Safari et Edge sur les appareils de bureau et mobiles.'
      },
      poker: {
        q1: 'Qu\'est-ce qu\'un mot valide ?',
        a1: 'Au moins 2 lettres. Nous validons via un service de dictionnaire en prévisualisation et après déploiement.',
        q2: 'Comment utiliser les lettres de la réserve publique ?',
        a2: 'En mode Coop, combinez les lettres de votre main et de la réserve. La réserve se remplit uniquement au début de chaque tour.',
        q3: 'Puis-je donner plusieurs lettres à la fois ?',
        a3: 'Oui. Sélectionnez une ou plusieurs lettres et cliquez Donner à la réserve. Elles seront immédiatement disponibles.'
      },
      sudoku: {
        q1: 'Comment résoudre un puzzle Sudoku ?',
        a1: 'Remplissez chaque ligne, colonne et boîte 3x3 avec les chiffres 1-9 sans répétition. Chaque chiffre apparaît exactement une fois dans chaque ligne, colonne et boîte.',
        q2: 'Puis-je obtenir des indices ?',
        a2: 'Oui ! Vous avez un nombre limité d\'indices par jeu. Utilisez-les judicieusement pour aider à résoudre les puzzles difficiles.',
        q3: 'Que se passe-t-il si je fais une erreur ?',
        a3: 'Vous pouvez utiliser le bouton Effacer pour supprimer une entrée, ou redémarrer le puzzle pour recommencer depuis le début.'
      },
      riddles: {
        q1: 'Combien de devinettes y a-t-il ?',
        a1: 'Nous avons une collection de devinettes à trois niveaux de difficulté. D\'autres devinettes sont ajoutées régulièrement.',
        q2: 'Puis-je sauter une devinette ?',
        a2: 'Oui, vous pouvez sauter des devinettes et passer à la suivante. Votre score de précision reflétera les devinettes ignorées.',
        q3: 'Que faire si je me trompe de réponse ?',
        a3: 'Vous pouvez réessayer ! Chaque devinette permet plusieurs tentatives pour que vous puissiez réfléchir sous différents angles.'
      }
    },
    leaderboard: {
      title: 'Mon Classement',
      subtitle: 'Suivez vos scores personnels et vos progrès dans tous les jeux',
      highScore: 'Meilleur score',
      totalGames: 'Jeux totaux',
      avgScore: 'Score moyen',
      winRate: 'Taux de victoire',
      gameHistory: 'Historique des jeux',
      noScores: 'Pas de scores encore. Commencez à jouer pour voir vos statistiques !',
      confirmReset: 'Êtes-vous sûr de vouloir réinitialiser tous les scores de ce jeu ?'
    },
    about: {
      title: 'À propos d\'UpGame',
      subtitle: 'En savoir plus sur notre mission et nos valeurs',
      mission: 'Notre mission',
      missionText: 'Chez UpGame, nous croyons que l\'apprentissage doit être amusant et attrayant. Notre mission est de créer des jeux éducatifs stimulants qui défient votre esprit tout en vous divertissant. Nous nous engageons à rendre l\'entraînement cérébral accessible à tous.',
      vision: 'Notre vision',
      visionText: 'Nous envisageons un monde où l\'éducation et le divertissement se fondent parfaitement, où des personnes de tous les âges peuvent améliorer leurs compétences cognitives par le biais de jeux attrayants.',
      features: 'Pourquoi choisir UpGame ?',
      feature1: {
        title: 'Gratuit et accessible',
        desc: 'Pas d\'inscription, pas d\'abonnement. Juste du pur plaisir de jeu !'
      },
      feature2: {
        title: 'Support multilingue',
        desc: 'Jouez en anglais, chinois ou français'
      },
      feature3: {
        title: 'Convivial pour mobile',
        desc: 'Jouez n\'importe quand, n\'importe où sur n\'importe quel appareil'
      },
      feature4: {
        title: 'Suivi des scores',
        desc: 'Surveillez vos progrès et vos améliorations'
      },
      ourGames: 'Nos jeux',
      getInTouch: 'Nous contacter',
      getInTouchText: 'Avez-vous des questions ou des commentaires ? Nous aimerions bien vous entendre !'
    },
    howToPlay: {
      title: 'Comment jouer',
      subtitle: 'Apprenez les règles et commencez avec UpGame',
      objectiveLabel: 'Objectif',
      howToPlayLabel: 'Comment jouer',
      rules: 'Règles',
      tips: 'Conseils',
      modes: 'Modes de jeu',
      difficulties: 'Niveaux de difficulté',
      classicMode: 'Mode classique',
      lightningMode: 'Mode Éclair',
      coopMode: 'Mode Coop',
      easyLevel: 'Facile',
      mediumLevel: 'Moyen',
      hardLevel: 'Difficile',
      ready: 'Prêt à jouer ?',
      poker: {
        objective: 'Construisez les mots les plus longs et les plus performants à partir des cartes de votre main',
        step1: 'Regardez les cartes dans votre main',
        step2: 'Arrangez les cartes pour former des mots anglais valides',
        step3: 'Cliquez sur "Valider le mot" pour soumettre et gagner des points',
        step4: 'Visez le score le plus élevé avant que le temps ne s\'écoule',
        classic: 'Prenez turns avec d\'autres joueurs pour construire des mots chaque manche. Le joueur avec le meilleur mot marque des points.',
        lightning: 'Battez l\'horloge ! Construisez des mots aussi vite que vous pouvez. Les bonus de points de vitesse !',
        coop: 'Travaillez ensemble ! Les deux joueurs contribuent des lettres à un pool partagé et coopèrent pour gagner.'
      },
      sudoku: {
        objective: 'Remplissez la grille pour que chaque ligne, colonne et case 3×3 contienne tous les chiffres 1-9',
        rule1: 'Chaque ligne doit contenir les chiffres 1 à 9 sans répétition',
        rule2: 'Chaque colonne doit contenir les chiffres 1 à 9 sans répétition',
        rule3: 'Chacune des neuf sous-grilles 3×3 doit contenir les chiffres 1 à 9 sans répétition',
        rule4: 'Certains chiffres sont donnés comme indices pour commencer',
        tips: 'Commencez par les lignes, colonnes ou cases avec moins d\'espaces vides. Cherchez les chiffres qui ne peuvent tenir qu\'à une place.'
      },
      riddles: {
        objective: 'Lisez chaque devinette et devinezla réponse correcte avant de passer à la suivante',
        step1: 'Lisez attentivement la devinette',
        step2: 'Pensez à la réponse et entrez-la',
        step3: 'Vérifiez votre réponse et passez à la devinette suivante',
        easy: 'Devinettes simples avec des indices clairs',
        medium: 'Devinettes qui nécessitent une pensée créative',
        hard: 'Devinettes de défi avec des jeux de mots délicats'
      }
    },
    tips: {
      title: 'Conseils et astuces',
      subtitle: 'Améliorez votre jeu avec les conseils stratégiques de nos experts',
      poker: {
        tip1: { title: 'Cherchez des combinaisons courantes', desc: 'Planifiez plusieurs mots en utilisant vos lettres disponibles pour maximiser les points.' },
        tip2: { title: 'Utilisez les lettres de haute valeur', desc: 'Les lettres comme Q, Z et X valent plus de points. Essayez de les utiliser stratégiquement.' },
        tip3: { title: 'Maîtrisez la formation des mots', desc: 'Pratiquez les modèles de mots courants pour former des mots plus rapidement.' },
        tip4: { title: 'Gérez votre temps', desc: 'En mode Éclair, la vitesse compte. Pratiquez la formation rapide de mots.' }
      },
      sudoku: {
        tip1: { title: 'Analysez les chiffres évidents', desc: 'Cherchez les cellules où un seul chiffre peut tenir.' },
        tip2: { title: 'Utilisez l\'élimination', desc: 'Marquez les chiffres possibles pour chaque cellule et éliminez progressivement les options.' },
        tip3: { title: 'Concentrez-vous sur les zones contraintes', desc: 'Travaillez d\'abord sur les lignes, colonnes ou cases avec moins d\'espaces vides first.' },
        tip4: { title: 'Prenez votre temps', desc: 'Le Sudoku concerne la logique, pas la vitesse. Réfléchissez bien avant de remplir une cellule.' }
      },
      riddles: {
        tip1: { title: 'Lisez attentivement', desc: 'Faites attention à chaque mot de la devinette. La réponse est souvent cachée dans la formulation.' },
        tip2: { title: 'Pensez littéralement et au sens figuré', desc: 'Les devinettes utilisent souvent du jeu de mots. Considérez à la fois les sens littéraux et métaphoriques.' },
        tip3: { title: 'Évitez les réponses courantes', desc: 'Les réponses aux devinettes ne sont généralement pas la première chose à laquelle on pense.' },
        tip4: { title: 'La pratique rend parfait', desc: 'Plus vous résolvez de devinettes, mieux vous détecterez les modèles.' }
      }
    },
    strategies: {
      title: 'Stratégies de Jeu',
      subtitle: 'Maîtrisez les jeux avec nos guides experts',
      readMore: 'Lire la suite',
      backToList: 'Retour aux stratégies',
      readyToPlay: 'Prêt pour le défi ?',
      notFound: 'Article non trouvé'
    },
    support: {
      title: 'Support et aide',
      subtitle: 'Nous sommes là pour vous aider avec vos questions',
      quickHelp: 'Aide rapide',
      faq: 'Foire aux questions',
      faqDesc: 'Trouvez des réponses aux questions les plus courantes sur UpGame',
      howToPlay: 'Comment jouer',
      howToPlayDesc: 'Apprenez les règles et comment commencer chaque jeu',
      tips: 'Conseils et astuces',
      tipsDesc: 'Améliorez vos compétences avec les conseils stratégiques de nos experts',
      commonIssues: 'Problèmes courants',
      stillNeedHelp: 'Avez-vous toujours besoin d\'aide ?',
      contactUsDesc: 'Si vous ne trouvez pas ce que vous recherchez, veuillez nous contacter.',
      issue1: { title: 'Les scores ne s\'enregistrent pas', solution: 'Assurez-vous que votre navigateur permet le stockage local. Vérifiez vos paramètres de confidentialité et assurez-vous que les cookies/stockage sont activés.' },
      issue2: { title: 'Le jeu ne se charge pas', solution: 'Essayez d\'actualiser la page ou d\'effacer le cache de votre navigateur. Assurez-vous que vous avez la dernière version de votre navigateur.' },
      issue3: { title: 'Les mots ne sont pas acceptés', solution: 'Assurez-vous que vous formez des mots anglais valides avec au minimum 2 lettres. Consultez notre dictionnaire pour connaître les mots acceptés.' },
      issue4: { title: 'Problèmes de performance', solution: 'Fermez les autres onglets et applications du navigateur. Désactivez les extensions de navigateur qui pourraient interférer avec les jeux.' }
    },
    contact: {
      title: 'Nous contacter',
      subtitle: 'Nous aimerions vous entendre. Contactez notre équipe.',
      sendMessage: 'Envoyez-nous un message',
      name: 'Nom',
      namePlaceholder: 'Votre nom',
      email: 'E-mail',
      emailPlaceholder: 'Votre adresse e-mail',
      subject: 'Sujet',
      selectSubject: 'Sélectionner un sujet',
      subjectBug: 'Rapport de bogue',
      subjectFeature: 'Demande de fonctionnalité',
      subjectFeedback: 'Commentaires généraux',
      subjectOther: 'Autre',
      message: 'Message',
      messagePlaceholder: 'Dites-nous ce que vous pensez...',
      getInTouch: 'Nous contacter',
      website: 'Site Internet',
      responseTime: 'Temps de réponse',
      responseTimeDesc: 'Nous répondons généralement aux messages dans les 24 heures',
      successMessage: 'Merci pour votre message ! Nous vous recontacterons bientôt.',
      errorMessage: 'Une erreur s\'est produite lors de l\'envoi de votre message. Veuillez réessayer.'
    },
    privacy: {
      title: 'Politique de confidentialité',
      lastUpdated: 'Dernière mise à jour : 01/01/2025',
      introduction: 'Introduction',
      introductionText: 'Chez UpGame (upgame.top), nous respectons votre confidentialité et nous nous engageons à protéger vos données personnelles. Cette Politique de confidentialité explique comment nous collectons, utilisons et protégeons vos informations.',
      dataCollection: 'Collecte de données',
      dataCollectionText: 'Nous collectons le minimum d\'informations personnelles pour fournir nos services. Toutes les données sont stockées localement sur votre appareil.',
      typesOfData: 'Types de données que nous collectons',
      data1: 'Scores de jeux et statistiques (stockés localement)',
      data2: 'Préférences de langue et de thème',
      data3: 'Informations sur le navigateur et l\'appareil pour les analyses',
      data4: 'Soumissions de formulaires de contact (email et message uniquement)',
      dataUsage: 'Comment nous utilisons vos données',
      dataUsageText: 'Vos données ne sont utilisées que pour améliorer votre expérience de jeu et nos services.',
      purposes: 'Objectifs de l\'utilisation des données',
      purpose1: 'Pour suivre la progression et les scores de votre jeu',
      purpose2: 'Pour mémoriser vos préférences et paramètres',
      purpose3: 'Pour répondre à vos soumissions de formulaires de contact',
      purpose4: 'Pour améliorer nos jeux en fonction des modèles d\'utilisation',
      dataStorage: 'Stockage des données',
      dataStorageText: 'La plupart de vos données sont stockées localement dans votre navigateur. Les soumissions de formulaires de contact sont stockées de manière sécurisée sur nos serveurs pendant une période limitée.',
      userRights: 'Vos droits',
      userRightsText: 'Vous avez le droit de contrôler vos informations personnelles. Voici ce que vous pouvez faire :',
      rights: 'Vos droits',
      right1: 'Accédez à vos données à tout moment via votre navigateur',
      right2: 'Supprimez vos données en effaçant votre stockage de navigateur',
      right3: 'Exportez vos données pour un usage personnel',
      right4: 'Refusez le suivi analytique dans les paramètres',
      contactUs: 'Nous contacter',
      contactUsText: 'Si vous avez des questions sur cette Politique de confidentialité, veuillez nous contacter :'
    },
    terms: {
      title: 'Conditions de service',
      lastUpdated: 'Dernière mise à jour : 01/01/2025',
      acceptance: 'Acceptation des conditions',
      acceptanceText: 'En accédant à et en utilisant UpGame, vous acceptez et consentez à être lié par les termes et conditions de cet accord. Si vous n\'acceptez pas de vous conformer aux conditions ci-dessus, veuillez ne pas utiliser ce service.',
      userAgreement: 'Accord utilisateur',
      userAgreementText: 'Les utilisateurs d\'UpGame acceptent d\'utiliser le service uniquement à des fins légales et d\'une manière qui ne porte pas atteinte aux droits d\'autrui ni ne restreint son utilisation et sa jouissance.',
      requirements: 'Conditions utilisateur',
      requirement1: 'Vous devez avoir au moins 13 ans pour utiliser UpGame',
      requirement2: 'Vous êtes responsable de la confidentialité de votre appareil',
      requirement3: 'Vous acceptez de ne pas vous engager dans des activités illégales ou nuisibles',
      requirement4: 'Vous ne tenterez pas d\'accéder à titre non autorisé au service',
      useRights: 'Licence limitée',
      useRightsText: 'UpGame vous accorde une licence limitée, non exclusive et non transférable pour utiliser le service uniquement à titre personnel et non commercial.',
      restrictions: 'Restrictions',
      restrictionsText: 'Vous acceptez de ne pas vous engager dans les conduites interdites suivantes :',
      prohibited: 'Activités interdites',
      prohibited1: 'Ingénierie inverse ou tentative de modification du service',
      prohibited2: 'Utiliser le service à des fins commerciales sans permission',
      prohibited3: 'Tentez d\'accéder à ou de falsifier l\'infrastructure du service',
      prohibited4: 'Créer plusieurs comptes pour contourner les limitations',
      intellectualProperty: 'Propriété intellectuelle',
      intellectualPropertyText: 'Tout le contenu, les fonctionnalités et les fonctionnalités d\'UpGame sont détenus par UpGame, ses concédants ou d\'autres fournisseurs de ce matériel et sont protégés par les lois sur le droit d\'auteur, les marques de commerce et autres propriété intellectuelle.',
      disclaimers: 'Clause de non-responsabilité',
      disclaimersText: 'UpGame est fourni sur une base "EN L\'ÉTAT" sans garantie d\'aucune sorte. Nous ne garantissons pas que le service sera ininterrompu ou sans erreur.',
      limitation: 'Limitation de responsabilité',
      limitationText: 'UpGame ne sera pas responsable de tous les dommages indirects, accidentels, spéciaux, consécutifs ou punitifs résultant de votre utilisation ou de votre incapacité à utiliser le service.',
      termination: 'Résiliation',
      terminationText: 'Nous pouvons résilier ou suspendre votre accès à UpGame à tout moment, pour quelque raison que ce soit, sans préavis ou responsabilité.',
      modifications: 'Modifications des conditions',
      modificationsText: 'Nous nous réservons le droit de modifier ces conditions à tout moment. Les modifications prendront effet immédiatement après la publication. Votre utilisation continue indique votre acceptation des conditions modifiées.',
      governingLaw: 'Loi applicable',
      governingLawText: 'Ces Conditions de service sont régies et interprétées conformément aux lois de la juridiction où UpGame est exploité.',
      contactUs: 'Nous contacter',
      contactUsText: 'Si vous avez des questions sur ces Conditions, veuillez nous contacter :'
    },
    footer: {
      description: 'Améliorez votre cerveau avec des jeux amusants et éducatifs. Défiez-vous avec des puzzles de mots, des jeux de chiffres et des devinettes.',
      games: 'Jeux',
      resources: 'Ressources',
      company: 'Entreprise',
      howToPlay: 'Comment jouer',
      tips: 'Conseils et astuces',
      faq: 'FAQ',
      support: 'Support',
      about: 'À propos de nous',
      contact: 'Contact',
      privacy: 'Politique de confidentialité',
      terms: 'Conditions de service',
      allRightsReserved: 'Tous droits réservés.',
      madeWith: 'Conçu avec',
      forLearning: 'pour l\'apprentissage et le plaisir',
      language: 'Langue'
    },
    dict: { loading: 'Chargement de la définition...', unavailable: 'Définition indisponible.', more: 'Plus', less: 'Moins' },
    cookie: {
      description: 'Nous utilisons des cookies pour améliorer votre expérience et analyser le trafic du site. En cliquant sur "Accepter", vous acceptez notre utilisation de cookies.',
      accept: 'Accepter',
      decline: 'Refuser'
    }
  }
}
